import { hasFailed } from "@miragon/react-utils-api";
import { Delete } from "@mui/icons-material";
import Edit from "@mui/icons-material/Edit";
import QATableButton from "components/Design/Table/Controls/QATableButton";
import React, { useCallback, useMemo } from "react";
import { useNavigate } from "react-router";
import QAButton from "../../components/Design/Controls/QAButton";
import QAButtonRow from "../../components/Design/Layout/QAButtonRow";
import QATableShortcut from "../../components/Design/Table/Controls/QATableShortcut";
import QATableButtonRow from "../../components/Design/Table/Layout/QATableButtonRow";
import QATable, { ColumnDefinition } from "../../components/Design/Table/QATable";
import ConfirmationDialog from "../../components/Hooks/Dialog/ConfirmationDialog";
import useDialog from "../../components/Hooks/Dialog/useDialog";
import useSnackbar from "../../components/Hooks/Snackbar/useSnackbar";
import { Routing } from "../../data/Constants/Routing";
import { useAppDispatch } from "../../state/Store";
import { ControllerAPI, RoleRO } from "../../api/api";
import { updateRolesManually } from "../../state/RolesState";

interface Props {
    roles: RoleRO[];
}

const RoleTable: React.FC<Props> = props => {
    const navigate = useNavigate();
    const snackbar = useSnackbar();
    const dialog = useDialog();
    const dispatch = useAppDispatch();

    const { roles } = props;

    const onDelete = useCallback(async (role: RoleRO) => {
        const confirmation = await dialog.open(ConfirmationDialog, {
            title: "Rolle löschen",
            question: `Wollen Sie die Rolle "${role.name}" wirklich löschen? Dies kann nicht rückgängig gemacht werden.`,
            acceptButtonTitle: "Löschen"
        });

        if (!confirmation.accepted) {
            return;
        }

        const response = await ControllerAPI.deleteRole(role.id);
        if (hasFailed(response)) {
            snackbar.onApiError(response);
        } else {
            dispatch(updateRolesManually({
                delete: [role]
            }));
            snackbar.onSuccess("Rolle gelöscht.");
        }
    }, [dialog, snackbar, dispatch]);

    const columns: ColumnDefinition<RoleRO>[] = useMemo(() => [
        {
            id: "group",
            title: "Gruppe",
            width: "250px",
            sortable: true,
            getValue: item => item.group,
            renderCell: role => {
                return (
                    <QATableShortcut target={Routing.Administration.Role.Edit.Navigate(role.id)}>
                        {role.group}
                    </QATableShortcut>
                );
            }
        },
        {
            id: "name",
            title: "Name",
            width: "auto",
            sortable: true,
            getValue: item => item.name,
            renderCell: role => {
                return (
                    <QATableShortcut target={Routing.Administration.Role.Edit.Navigate(role.id)}>
                        {role.name}
                    </QATableShortcut>
                );
            }
        },
        {
            id: "actions",
            width: "70px",
            padding: 0,
            getValue: () => undefined,
            renderCell: role => (
                <QATableButtonRow>
                    <QATableButton
                        icon={Edit}
                        onClicked={() => navigate(Routing.Administration.Role.Edit.Navigate(role.id))} />
                    <QATableButton
                        icon={Delete}
                        onClicked={() => onDelete(role)} />
                </QATableButtonRow>
            )
        }
    ], [navigate, onDelete]);

    return (
        <>

            <QAButtonRow alignRight marginBottom>
                <QAButton
                    light
                    size="small"
                    title="Erstellen"
                    onClicked={() => navigate(Routing.Administration.Role.Create.Navigate())} />
            </QAButtonRow>

            <QATable
                defaultSortByColumn="name"
                items={roles}
                defaultPageSize={25}
                getId={role => role.id}
                fallbackText="Keine Rollen gefunden."
                columns={columns} />

        </>

    );
};

export default RoleTable;
